import React from 'react';
import {
  Collection as AriaCollection,
  Header as AriaHeader,
  ListBoxSection as AriaListBoxSection,
} from 'react-aria-components';

import { forwardRef } from '../../generic-forward-ref';
import { rowStyles } from '../overlay/row';

interface OptionSectionCommonProps {
  /** The heading over the group's options, and the group's accessible name. */
  title: string;
}

interface OptionSectionStaticProps extends OptionSectionCommonProps {
  items?: never;
  /** The group's Option elements. */
  children: React.ReactNode;
}

interface OptionSectionDynamicProps<T> extends OptionSectionCommonProps {
  /** The group's data; each item renders through `children`. Keep the array's identity stable. */
  items: Iterable<T>;
  /** Renders one item as an Option. */
  children: (item: T) => React.ReactElement;
}

export type OptionSectionProps<T = object> =
  | OptionSectionStaticProps
  | OptionSectionDynamicProps<T>;

/**
 * A titled group of options inside a ListBox, a Select or a Combobox (the
 * MenuSection precedent): the heading names the group and reads as a label
 * over the options, never as an option, and React Aria keeps it out of the
 * arrow-key order.
 */
export const OptionSection = forwardRef(function OptionSection<T extends object>(
  { title, items, children }: OptionSectionProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const { sectionHeading } = rowStyles();
  return (
    <AriaListBoxSection ref={ref} data-component="option-section">
      <AriaHeader className={sectionHeading()}>{title}</AriaHeader>
      {items ? (
        <AriaCollection items={items}>{children as (item: T) => React.ReactElement}</AriaCollection>
      ) : (
        children
      )}
    </AriaListBoxSection>
  );
});
